import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  Checkbox,
  ErrorState,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  Select,
  TextInput,
  useTranslate,
} from '@athena/ui'
import { useRef, useState } from 'react'
import {
  AiExperimentConfigurationDocument,
  ConfigureAiExperimentDocument,
  RegisterAiModelPricingDocument,
  type AiExperimentConfigurationQuery,
} from '../../graphql/generated/operations.js'
import { ReasonField, UnsavedChanges } from '../management/ManagementSupport.js'
import { useFocusFirstInvalid } from '../../lib/validation.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'
import { aiFormatting, providerName } from '../../lib/ai-analytics.js'
import { useLanguage } from '@athena/ui'
import { DataTable } from '@athena/ui'
import { formatDateTime } from '../../lib/display.js'

export function AIConfiguration() {
  const t = useTranslate()
  const client = useApolloClient()
  const [confirmed, setConfirmed] = useState(false)
  const { data, loading, error, refetch } = useQuery(AiExperimentConfigurationDocument, {
    fetchPolicy: 'network-only',
  })
  return (
    <Section id="ai-experiment" title={t('aiAnalytics.experiment')}>
      <p className="text-body-sm text-text-secondary">{t('aiAnalytics.configHelp')}</p>
      {loading && !data ? <LoadingState>{t('common.loading')}</LoadingState> : null}
      {error ? <ErrorState>{t('admin.loadError')}</ErrorState> : null}
      {data ? (
        <ExperimentForm
          key={data.adminAIExperiment.version}
          configuration={data.adminAIExperiment}
          reload={() => refetch()}
          onEdited={() => setConfirmed(false)}
          onSaved={(configuration) => {
            setConfirmed(true)
            client.cache.updateQuery({ query: AiExperimentConfigurationDocument }, (current) =>
              current ? { ...current, adminAIExperiment: configuration } : current,
            )
          }}
        />
      ) : null}
      {confirmed ? <InlineConfirmation>{t('aiAnalytics.saved')}</InlineConfirmation> : null}
      <PricingForm />
      {data?.adminAIModelPricing.length ? <PricingTable rows={data.adminAIModelPricing} /> : null}
    </Section>
  )
}
function ExperimentForm({
  configuration,
  reload,
  onSaved,
  onEdited,
}: {
  configuration: AiExperimentConfigurationQuery['adminAIExperiment']
  reload: () => Promise<unknown>
  onSaved: (configuration: AiExperimentConfigurationQuery['adminAIExperiment']) => void
  onEdited: () => void
}) {
  const t = useTranslate()
  const errorText = useFirstUserErrorText()
  const [enabled, setEnabled] = useState(configuration.enabled)
  const [openai, setOpenai] = useState(String(configuration.openaiWeight))
  const [gemini, setGemini] = useState(String(configuration.geminiWeight))
  const [reason, setReason] = useState('')
  const [validation, setValidation] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const submitting = useRef(false)
  const focus = useFocusFirstInvalid<HTMLFormElement>()
  const [save] = useMutation(ConfigureAiExperimentDocument)
  const validWeights =
    openai.trim() !== '' &&
    gemini.trim() !== '' &&
    [Number(openai), Number(gemini)].every((v) => Number.isInteger(v) && v >= 0 && v <= 100) &&
    Number(openai) + Number(gemini) === 100
  const dirty =
    !saved &&
    (enabled !== configuration.enabled ||
      openai !== String(configuration.openaiWeight) ||
      gemini !== String(configuration.geminiWeight) ||
      reason !== '')
  async function submit() {
    if (submitting.current) return
    setValidation(true)
    setSaved(false)
    setError('')
    if (!validWeights || !reason.trim()) {
      focus.request()
      return
    }
    submitting.current = true
    setSaving(true)
    try {
      const result = await save({
        variables: {
          input: {
            enabled,
            openaiWeight: Number(openai),
            geminiWeight: Number(gemini),
            expectedVersion: configuration.version,
            experimentId: configuration.experimentId,
            reason: reason.trim(),
          },
        },
      })
      const payload = result.data?.adminConfigureAIExperiment
      if (payload?.userErrors.length) {
        setError(errorText(payload.userErrors, t('aiAnalytics.saveError')))
        return
      }
      if (!payload?.configuration) {
        setError(t('aiAnalytics.saveError'))
        return
      }
      setSaved(true)
      onSaved(payload.configuration)
    } catch {
      setError(t('aiAnalytics.saveError'))
    } finally {
      submitting.current = false
      setSaving(false)
    }
  }
  return (
    <form
      ref={focus.ref}
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      className="flex flex-col gap-4"
    >
      <UnsavedChanges dirty={dirty} />
      <p className="text-body-sm">
        {t('aiAnalytics.effective', [
          configuration.openaiWeight,
          configuration.geminiWeight,
          configuration.version,
        ])}
      </p>
      <Checkbox
        label={t('aiAnalytics.enabled')}
        checked={enabled}
        onCheckedChange={(v) => {
          setEnabled(v === true)
          onEdited()
        }}
        disabled={saving}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label={t('aiAnalytics.openaiWeight')}
          type="number"
          min={0}
          max={100}
          step={1}
          required
          value={openai}
          onChange={(e) => {
            setOpenai(e.target.value)
            onEdited()
          }}
          disabled={saving}
          error={validation && !validWeights ? t('aiAnalytics.invalidWeights') : ''}
        />
        <TextInput
          label={t('aiAnalytics.geminiWeight')}
          type="number"
          min={0}
          max={100}
          step={1}
          required
          value={gemini}
          onChange={(e) => {
            setGemini(e.target.value)
            onEdited()
          }}
          disabled={saving}
          error={validation && !validWeights ? t('aiAnalytics.invalidWeights') : ''}
        />
      </div>
      {enabled &&
      ((Number(openai) > 0 && !configuration.openaiAvailable) ||
        (Number(gemini) > 0 && !configuration.geminiAvailable)) ? (
        <Notice tone="warning" title={t('aiAnalytics.keyMissing')} />
      ) : null}
      <ReasonField
        value={reason}
        onChange={(value) => {
          setReason(value)
          onEdited()
        }}
        error={validation && !reason.trim() ? t('management.errors.reason') : ''}
      />
      {error ? <Notice tone="error" title={error} /> : null}
      <div className="flex flex-wrap gap-3">
        <Button type="submit" loading={saving} disabled={!dirty}>
          {t('aiAnalytics.save')}
        </Button>
        {error ? (
          <Button variant="secondary" onClick={() => void reload()}>
            {t('aiAnalytics.reload')}
          </Button>
        ) : null}
      </div>
    </form>
  )
}
function PricingForm() {
  const t = useTranslate()
  const [register, { loading }] = useMutation(RegisterAiModelPricingDocument, {
    refetchQueries: [AiExperimentConfigurationDocument],
  })
  const [values, setValues] = useState({
    provider: 'openai',
    model: '',
    version: '',
    input: '',
    output: '',
    cache: '',
    write: '',
    source: '',
    reason: '',
  })
  const [invalid, setInvalid] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const submitting = useRef(false)
  const focus = useFocusFirstInvalid<HTMLFormElement>()
  const rateValid = (v: string, optional = false) =>
    (optional && v.trim() === '') ||
    (v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 1000000)
  const valid = Boolean(
    values.model.trim() &&
    values.version.trim() &&
    values.reason.trim() &&
    /^https:\/\//.test(values.source) &&
    rateValid(values.input) &&
    rateValid(values.output) &&
    rateValid(values.cache, true) &&
    rateValid(values.write, true),
  )
  const set = (key: keyof typeof values, value: string) => {
    setSaved(false)
    setValues((v) => ({ ...v, [key]: value }))
  }
  return (
    <Section id="ai-pricing" title={t('aiAnalytics.pricing')}>
      <p className="text-body-sm text-text-secondary">{t('aiAnalytics.priceHelp')}</p>
      <form
        ref={focus.ref}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (submitting.current) return
          setInvalid(true)
          setSaved(false)
          setError('')
          if (!valid) {
            focus.request()
            return
          }
          submitting.current = true
          void register({
            variables: {
              input: {
                provider: values.provider,
                model: values.model.trim(),
                version: values.version.trim(),
                effectiveAt: new Date().toISOString(),
                inputUsdPerMillion: Number(values.input),
                outputUsdPerMillion: Number(values.output),
                cachedInputUsdPerMillion: values.cache.trim() === '' ? null : Number(values.cache),
                cacheWriteUsdPerMillion: values.write.trim() === '' ? null : Number(values.write),
                sourceUrl: values.source.trim(),
                reason: values.reason.trim(),
              },
            },
          })
            .then(() => {
              setSaved(true)
              setInvalid(false)
              setValues((v) => ({ ...v, reason: '' }))
            })
            .catch(() => setError(t('aiAnalytics.saveError')))
            .finally(() => {
              submitting.current = false
            })
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2 desktop:grid-cols-3">
          <Select
            label={t('aiAnalytics.provider')}
            value={values.provider}
            onValueChange={(v) => set('provider', v)}
            options={[
              { value: 'openai', label: 'OpenAI' },
              { value: 'gemini', label: 'Gemini' },
            ]}
            disabled={loading}
          />
          {(['model', 'version', 'input', 'output', 'cache', 'write', 'source'] as const).map(
            (key) => (
              <TextInput
                key={key}
                label={t(
                  (
                    {
                      model: 'aiAnalytics.model',
                      version: 'aiAnalytics.priceVersion',
                      input: 'aiAnalytics.inputRate',
                      output: 'aiAnalytics.outputRate',
                      cache: 'aiAnalytics.cacheRate',
                      write: 'aiAnalytics.cacheWriteRate',
                      source: 'aiAnalytics.sourceUrl',
                    } as const
                  )[key],
                )}
                value={values[key]}
                onChange={(e) => set(key, e.target.value)}
                disabled={loading}
                type={
                  ['input', 'output', 'cache', 'write'].includes(key)
                    ? 'number'
                    : key === 'source'
                      ? 'url'
                      : 'text'
                }
                step="any"
                min={0}
                optional={key === 'cache' || key === 'write'}
                required={key !== 'cache' && key !== 'write'}
                maxLength={key === 'source' ? 1000 : 200}
                error={invalid && !valid ? t('aiAnalytics.invalidPricing') : ''}
              />
            ),
          )}
        </div>
        <ReasonField
          value={values.reason}
          onChange={(v) => set('reason', v)}
          error={invalid && !values.reason.trim() ? t('management.errors.reason') : ''}
        />
        {error ? <Notice tone="error" title={error} /> : null}
        {saved ? <InlineConfirmation>{t('aiAnalytics.priceSaved')}</InlineConfirmation> : null}
        <Button type="submit" loading={loading} className="self-start">
          {t('aiAnalytics.registerPrice')}
        </Button>
      </form>
    </Section>
  )
}
function PricingTable({ rows }: { rows: AiExperimentConfigurationQuery['adminAIModelPricing'] }) {
  const t = useTranslate()
  const language = useLanguage()
  const format = aiFormatting(language, t)
  return (
    <DataTable
      caption={t('aiAnalytics.pricing')}
      rows={rows}
      rowKey={(r) => r.id}
      columns={[
        {
          id: 'model',
          header: t('aiAnalytics.model'),
          cell: (r) => (
            <span className="break-words">
              {providerName(r.provider)} · {r.model}
            </span>
          ),
        },
        { id: 'version', header: t('aiAnalytics.priceVersion'), cell: (r) => r.version },
        {
          id: 'input',
          header: t('aiAnalytics.inputRate'),
          cell: (r) => format.money(r.inputUsdPerMillion),
        },
        {
          id: 'output',
          header: t('aiAnalytics.outputRate'),
          cell: (r) => format.money(r.outputUsdPerMillion),
        },
        {
          id: 'date',
          header: t('aiAnalytics.effectiveAt'),
          cell: (r) => formatDateTime(r.effectiveAt, language),
          priority: 'secondary',
        },
      ]}
    />
  )
}
